import { z } from 'zod';
import { categoryBoundingBox, product } from '../Product';
import { fieldFilter } from '../FieldFilter';
import { categoryFilter } from '../CategoryFilter';
import { productGroupSummary } from '../product/ProductGroupSummary';
import { productComparisonResult } from '../assistant/ProductComparisonTable';
import { productSearchSuggestions } from '../ProductSearchSuggestion';

export const featureType = z.enum([
    'product-search-by-text',
    'product-search-new',
    'product-search-similar',
    'product-search-similar-by-image',
    'product-search-combinations',
    'product-search-combinations-by-image',
    'product-search-complementary-category',
    'product-mix-and-match',
    'product-comparison',
    'product-detail',
    'product-multi-variant-add-to-cart',
    'product-search-by-image'
]);

export const appliedFilters = z.object({
    appliedMaxPrice: z.number().nullable().default(null),
    appliedMinPrice: z.number().nullable().default(null),
    appliedCategoryFilters: z.array(categoryFilter).default([]),
    appliedFieldFilters: z.array(fieldFilter).default([]),
    appliedSortBy: z
        .enum(['relevance', 'price-asc', 'price-desc'])
        .default('relevance')
});

export const availableFilters = z.object({
    availableMinPrice: z.number().nullable().default(null),
    availableMaxPrice: z.number().nullable().default(null),
    availableFieldFilters: z.array(fieldFilter),
    availableCategoryFilters: z.array(categoryFilter)
});

export const filtersContext = z.object({
    ...availableFilters.shape,
    ...appliedFilters.shape
});

export const resultWithSuggestedProducts = z.object({
    suggestedProducts: z.array(product),
    suggestedFieldFilters: z.array(fieldFilter),
    suggestedCategoryFilters: z.array(categoryFilter)
});

export const baseChatContext = z.object({
    featureUuid: z.string()
});

export const productSearchByTextChatContext = z.object({
    ...baseChatContext.shape,
    ...filtersContext.shape,
    ...resultWithSuggestedProducts.shape,
    featureType: z.literal('product-search-by-text'),
    searchText: z.string(),
    isValidSearch: z.boolean().optional(),
    baseFieldFilters: z.array(fieldFilter),
    baseCategoryFilters: z.array(categoryFilter),
    totalQuantity: z.number().int().nonnegative().optional(),
    products: z.array(product).optional(),
    searchSuggestions: z.array(productSearchSuggestions).optional()
});

export const productSearchByImageChatContext = z.object({
    ...baseChatContext.shape,
    featureType: z.literal('product-search-by-image'),
    imageDataUrl: z.string(),
    imageId: z.string().optional(),
    imageUrl: z.string().optional(),
    boundingBoxes: categoryBoundingBox.array().optional(),
    imageWidth: z.number().optional(),
    imageHeight: z.number().optional()
});

export const productSearchSimilarChatContext = z.object({
    ...baseChatContext.shape,
    ...filtersContext.shape,
    ...resultWithSuggestedProducts.shape,
    featureType: z.literal('product-search-similar'),
    baseProducts: z.array(product),
    baseProductsIds: z.array(z.string()),
    totalQuantity: z.number().int().nonnegative().optional(),
    baseFieldFilters: z.array(fieldFilter),
    baseCategoryFilters: z.array(categoryFilter),
    products: z.array(product)
});

export const productSearchCombinationsChatContext = z.object({
    ...baseChatContext.shape,
    ...filtersContext.shape,
    ...resultWithSuggestedProducts.shape,
    featureType: z.literal('product-search-combinations'),
    baseProducts: z.array(product),
    baseProductsIds: z.array(z.string()),
    groups: z.array(productGroupSummary)
});

export const productSearchComplementaryCategoryChatContext = z.object({
    ...baseChatContext.shape,
    ...filtersContext.shape,
    ...resultWithSuggestedProducts.shape,
    featureType: z.literal('product-search-complementary-category'),
    baseProducts: z.array(product),
    categoryFilter,
    totalQuantity: z.number().int().nonnegative().optional(),
    products: z.array(product)
});

export const productMixAndMatchChatContext = z.object({
    ...baseChatContext.shape,
    featureType: z.literal('product-mix-and-match'),
    baseProducts: z.array(product),
    baseProductsIds: z.array(z.string()),
    groups: z
        .object({
            title: z.string(),
            subtitle: z.string(),
            description: z.string(),
            tips: z.array(z.string()),
            categorizedGroups: z.array(
                z.object({
                    categoryFilter: categoryFilter.nullable().default(null),
                    products: z.array(product)
                })
            )
        })
        .array()
});

export const productSearchNewChatContext = z.object({
    ...baseChatContext.shape,
    ...filtersContext.shape,
    ...resultWithSuggestedProducts.shape,
    featureType: z.literal('product-search-new'),
    maxDiscountedPercentage: z.number().optional(),
    minDiscountedPercentage: z.number().optional(),
    withAllTags: z.array(z.string()).optional(),
    withSomeTags: z.array(z.string()).optional(),
    withNoneTags: z.array(z.string()).optional(),
    products: z.array(product),
    totalQuantity: z.number().int().nonnegative().optional()
});

export const productSearchSimilarByImageChatContext = z.object({
    ...baseChatContext.shape,
    ...filtersContext.shape,
    ...resultWithSuggestedProducts.shape,
    featureType: z.literal('product-search-similar-by-image'),
    imageId: z.string(),
    imageUrl: z.string(),
    baseFieldFilters: z.array(fieldFilter),
    baseCategoryFilters: z.array(categoryFilter),
    totalQuantity: z.number().int().nonnegative().optional(),
    products: z.array(product)
});

export const productSearchCombinationsByImageChatContext = z.object({
    ...baseChatContext.shape,
    ...filtersContext.shape,
    ...resultWithSuggestedProducts.shape,
    featureType: z.literal('product-search-combinations-by-image'),
    imageId: z.string(),
    imageUrl: z.string(),
    groups: z.array(productGroupSummary)
});

export const productComparisonChatContext = z.object({
    ...baseChatContext.shape,
    featureType: z.literal('product-comparison'),
    table: productComparisonResult.nullable().default(null),
    comparingProductIds: z.array(z.string()),
    comparingProducts: z.array(product)
});

export const productDetailChatContext = z.object({
    ...baseChatContext.shape,
    featureType: z.literal('product-detail'),
    productId: z.string(),
    product: product.optional(),
    combinations: z.array(product).optional(),
    similar: z.array(product).optional()
});

export const productMultiVariantAddToCartChatContext = z.object({
    ...baseChatContext.shape,
    featureType: z.literal('product-multi-variant-add-to-cart'),
    products: z.array(product)
});

export const chatContext = z.discriminatedUnion('featureType', [
    productSearchByTextChatContext,
    productSearchByImageChatContext,
    productSearchSimilarChatContext,
    productSearchCombinationsChatContext,
    productSearchComplementaryCategoryChatContext,
    productMixAndMatchChatContext,
    productSearchNewChatContext,
    productSearchSimilarByImageChatContext,
    productSearchCombinationsByImageChatContext,
    productComparisonChatContext,
    productDetailChatContext,
    productMultiVariantAddToCartChatContext
]);

export type FeatureType = z.infer<typeof featureType>;
export type AppliedFilters = z.infer<typeof appliedFilters>;
export type AvailableFilters = z.infer<typeof availableFilters>;
export type FiltersContext = z.infer<typeof filtersContext>;
export type ResultWithSuggestedProducts = z.infer<
    typeof resultWithSuggestedProducts
>;
export type ProductSearchByTextChatContext = z.infer<
    typeof productSearchByTextChatContext
>;
export type ProductSearchSimilarChatContext = z.infer<
    typeof productSearchSimilarChatContext
>;
export type ProductSearchCombinationsChatContext = z.infer<
    typeof productSearchCombinationsChatContext
>;
export type ProductSearchComplementaryCategoryChatContext = z.infer<
    typeof productSearchComplementaryCategoryChatContext
>;
export type ProductMixAndMatchChatContext = z.infer<
    typeof productMixAndMatchChatContext
>;
export type ProductSearchNewChatContext = z.infer<
    typeof productSearchNewChatContext
>;
export type ProductSearchSimilarByImageChatContext = z.infer<
    typeof productSearchSimilarByImageChatContext
>;
export type ProductSearchCombinationsByImageChatContext = z.infer<
    typeof productSearchCombinationsByImageChatContext
>;
export type ProductComparisonChatContext = z.infer<
    typeof productComparisonChatContext
>;
export type ProductDetailChatContext = z.infer<typeof productDetailChatContext>;
export type ProductMultiVariantAddToCartChatContext = z.infer<
    typeof productMultiVariantAddToCartChatContext
>;

export type ChatContext = z.infer<typeof chatContext>;
